import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { ApiError } from '../../abstract/api-error';
import { MarketplacePayment } from '../../core/data-models/marketplace-payment.model';
import { MarketplacePaymentService } from '../../core/data-services/marketplace-payment.service';
import { MarketplacePaymentStatus } from '../../core/data-models/marketplace-payment-status.model';
import { formatPaymentAcres, formatPaymentDateTime, formatPaymentMoney, shortPaymentId } from '../../shared/utils/payment-format.utils';

/**
 * Admin "All Payments" tab — diagnostic / search view spanning every
 * marketplace_payments row regardless of status.
 *
 * Spec: /agent-os/specs/2026-06-10-deferred-capture-admin-approval/spec.md (§ 5.3.1)
 *
 * Distinct from Pending Approval (which is the actionable queue scoped
 * to pending_admin_approval + failed). This view is for "I need to look
 * up the payment for job XYZ regardless of where it is in its
 * lifecycle." Default sort: newest first.
 *
 * Filters:
 *   - Status (single select; "" = All statuses)
 *   - Refresh button
 *
 * Row tap → payment-detail page. Mirrors the pattern from
 * pending-approval-list + disputes-list. (2026-06-11)
 */
@Component({
  selector: 'app-all-payments-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  template: `
    <div class="tab-pane">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h5 class="mb-1">All Payments</h5>
          <small class="text-muted">
            Showing {{ statusLabel() }} payments. Newest first.
          </small>
        </div>
        <div class="d-flex gap-2 align-items-center flex-wrap">
          <select
            class="form-select form-select-sm"
            [(ngModel)]="statusFilter"
            (change)="reload()"
            [ngModelOptions]="{standalone: true}"
            [disabled]="loading"
            style="width: auto;">
            <option value="">All statuses</option>
            <option value="agreed_pending">Awaiting actuals</option>
            <option value="pending_admin_approval">Pending approval</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending capture</option>
            <option value="captured">Captured</option>
            <option value="payout_pending">Payout pending</option>
            <option value="transferred">Paid out</option>
            <option value="disputed">Disputed</option>
            <option value="refunded">Refunded</option>
            <option value="partially_refunded">Partially refunded</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            (click)="reload()"
            [disabled]="loading">
            <i class="bi bi-arrow-clockwise me-1"></i>Refresh
          </button>
        </div>
      </div>

      @if (loading) {
        <div class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      } @else if (loadError) {
        <div class="alert alert-danger">{{ loadError }}</div>
      } @else if (payments.length === 0) {
        <div class="text-center py-5 text-muted">
          <i class="bi bi-receipt display-4 d-block mb-2 text-secondary"></i>
          <p class="mb-0">No payments match the current filter.</p>
        </div>
      } @else {
        <div class="table-responsive">
          <table class="table data-table table-hover align-middle">
            <thead>
              <tr>
                <th>Status</th>
                <th>Created</th>
                <th class="text-end">Total</th>
                <th>Acres</th>
                <th>Job</th>
                <th class="text-end"></th>
              </tr>
            </thead>
            <tbody>
              @for (payment of payments; track payment.id) {
                <tr
                  class="cursor-pointer"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'Open payment ' + shortId(payment.id)"
                  [routerLink]="['/marketplace-payments/payment', payment.id]"
                  (keydown.enter)="goToPayment(payment.id)"
                  (keydown.space)="$event.preventDefault(); goToPayment(payment.id)">
                  <td>
                    <span [class]="statusBadgeClass(payment.status)">
                      {{ statusLabelFor(payment.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(payment.createdAt) }}</td>
                  <td class="text-end fw-semibold">{{ formatMoney(payment.totalChargeAmount) }}</td>
                  <td>{{ formatAcres(payment.billableAcres) }}</td>
                  <td class="text-muted small">{{ shortId(payment.jobId) }}</td>
                  <td class="text-end">
                    <a
                      class="btn btn-sm btn-outline-primary"
                      [routerLink]="['/marketplace-payments/payment', payment.id]"
                      (click)="$event.stopPropagation()">
                      View
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <!--
          Pagination: simple "Load more" when the latest page came back
          full. Avoids a numbered pager + total-count query (which the
          Hub doesn't expose). Same pattern as the disputes list. The
          200-row cap on the Hub backstops this in case an admin
          repeatedly clicks Load more.
        -->
        @if (canLoadMore) {
          <div class="text-center mt-3">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              (click)="loadMore()"
              [disabled]="loadingMore">
              @if (loadingMore) {
                <span class="spinner-border spinner-border-sm me-2"></span>
                Loading...
              } @else {
                Load more
              }
            </button>
          </div>
        }
      }
    </div>
  `,
})
export class AllPaymentsListComponent implements OnInit {
  payments: MarketplacePayment[] = [];
  loading = true;
  loadingMore = false;
  loadError: string | null = null;
  // '' = "All statuses" option; otherwise a valid MarketplacePaymentStatus.
  statusFilter: MarketplacePaymentStatus | '' = '';
  private pageSize = 50;
  // Tracks whether the LAST page returned was full — if so, more rows
  // may exist on the server. Flips false on the first short page.
  canLoadMore = false;

  constructor(
    private service: MarketplacePaymentService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  goToPayment(id: string): void {
    this.router.navigate(['/marketplace-payments/payment', id]);
  }

  reload(): void {
    this.loading = true;
    this.loadError = null;
    this.payments = [];
    this.canLoadMore = false;
    this.service
      .listAllPayments({
        statuses: this.statusFilter ? [this.statusFilter] : undefined,
        limit: this.pageSize,
        offset: 0,
      })
      .subscribe({
        next: (payments) => {
          this.payments = payments;
          this.canLoadMore = payments.length >= this.pageSize;
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.loadError = ApiError.fromHttpError(err).message;
        },
      });
  }

  loadMore(): void {
    if (this.loadingMore || !this.canLoadMore) return;
    this.loadingMore = true;
    this.service
      .listAllPayments({
        statuses: this.statusFilter ? [this.statusFilter] : undefined,
        limit: this.pageSize,
        offset: this.payments.length,
      })
      .subscribe({
        next: (more) => {
          this.payments = [...this.payments, ...more];
          this.canLoadMore = more.length >= this.pageSize;
          this.loadingMore = false;
        },
        error: (err) => {
          this.loadingMore = false;
          this.loadError = ApiError.fromHttpError(err).message;
        },
      });
  }

  statusLabel(): string {
    if (!this.statusFilter) return 'all';
    return this.statusLabelFor(this.statusFilter);
  }

  statusLabelFor(status: string | null | undefined): string {
    if (!status) return '—';
    const map: Record<string, string> = {
      agreed_pending: 'Awaiting actuals',
      pending_admin_approval: 'Pending approval',
      pending: 'Pending capture',
      captured: 'Captured',
      payout_pending: 'Payout pending',
      transferred: 'Paid out',
      disputed: 'Disputed',
      refunded: 'Refunded',
      partially_refunded: 'Partially refunded',
      failed: 'Failed',
      cancelled: 'Cancelled',
    };
    return map[status] || status;
  }

  statusBadgeClass(status: string | null | undefined): string {
    if (status === 'pending_admin_approval') return 'badge bg-warning text-dark';
    if (status === 'agreed_pending') return 'badge bg-info text-dark';
    if (status === 'failed') return 'badge bg-danger';
    if (status === 'captured' || status === 'payout_pending') return 'badge bg-primary';
    if (status === 'transferred') return 'badge bg-success';
    if (status === 'refunded' || status === 'partially_refunded') return 'badge bg-info text-dark';
    if (status === 'disputed') return 'badge bg-warning text-dark';
    if (status === 'cancelled') return 'badge bg-secondary';
    return 'badge bg-secondary';
  }

  shortId(id: string | null | undefined): string {
    return shortPaymentId(id);
  }

  formatDate(value: string | Date | null | undefined): string {
    return formatPaymentDateTime(value);
  }

  formatMoney(v: number | null | undefined): string {
    return formatPaymentMoney(v);
  }

  formatAcres(v: number | null | undefined): string {
    return formatPaymentAcres(v);
  }
}
